import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getThreads } from '../services/teacher';
import { getTeacherInboxWebSocketUrl } from '../utils/teacherWebSocket';
import { useAuth } from './AuthContext';

const TeacherMessagingContext = createContext(null);

export function useTeacherMessaging() {
  const ctx = useContext(TeacherMessagingContext);
  if (!ctx) {
    throw new Error('useTeacherMessaging must be used within TeacherMessagingProvider');
  }
  return ctx;
}

/** Optional: use outside strict provider (e.g. header badge) */
export function useTeacherMessagingOptional() {
  return useContext(TeacherMessagingContext);
}

const TEACHER_ROLES = ['teacher', 'admin', 'superadmin'];

export function TeacherMessagingProvider({ children }) {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '' });
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const notifyHandlerRef = useRef(null);

  const canConnect = user && TEACHER_ROLES.includes(user.role);

  const recomputeUnread = useCallback((list) => {
    const n = (list || []).filter((t) => t.unread).length;
    setUnreadCount(n);
  }, []);

  const refreshThreads = useCallback(async () => {
    if (!canConnect) return;
    try {
      const res = await getThreads();
      const list = res.data?.results || res.data || [];
      setThreads(list);
      recomputeUnread(list);
    } catch {
      /* keep previous */
    }
  }, [canConnect, recomputeUnread]);

  const registerNotifyHandler = useCallback((fn) => {
    notifyHandlerRef.current = fn;
    return () => {
      if (notifyHandlerRef.current === fn) {
        notifyHandlerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!canConnect) {
      setThreads([]);
      setUnreadCount(0);
      return undefined;
    }

    const token = localStorage.getItem('access_token');
    const url = getTeacherInboxWebSocketUrl(token);
    if (!url) {
      return undefined;
    }

    let closed = false;

    const clearTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      clearTimer();
      const delay = Math.min(30000, 1000 * 2 ** reconnectAttemptRef.current);
      reconnectTimerRef.current = setTimeout(() => {
        reconnectAttemptRef.current += 1;
        connect();
      }, delay);
    };

    function connect() {
      if (closed) return;
      try {
        wsRef.current?.close();
      } catch {
        /* ignore */
      }
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        setWsConnected(true);
        refreshThreads();
      };

      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === 'teacher.notify') {
            refreshThreads();
            const name = data.parent_name ? `New message from ${data.parent_name}` : 'New message received';
            setToast({ open: true, message: name });
            if (notifyHandlerRef.current) {
              notifyHandlerRef.current(data);
            }
          }
        } catch {
          /* ignore */
        }
      };

      ws.onclose = () => {
        setWsConnected(false);
        if (!closed) {
          scheduleReconnect();
        }
      };

      ws.onerror = () => {
        /* onclose will reconnect */
      };
    }

    connect();

    return () => {
      closed = true;
      clearTimer();
      try {
        wsRef.current?.close();
      } catch {
        /* ignore */
      }
      wsRef.current = null;
    };
  }, [canConnect, refreshThreads]);

  const closeToast = useCallback(() => {
    setToast((t) => ({ ...t, open: false }));
  }, []);

  const value = useMemo(
    () => ({
      threads,
      setThreads,
      unreadCount,
      refreshThreads,
      wsConnected,
      toast,
      closeToast,
      registerNotifyHandler,
      recomputeUnread,
    }),
    [
      threads,
      unreadCount,
      refreshThreads,
      wsConnected,
      toast,
      closeToast,
      registerNotifyHandler,
      recomputeUnread,
    ]
  );

  return (
    <TeacherMessagingContext.Provider value={value}>{children}</TeacherMessagingContext.Provider>
  );
}
